import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Gift guard (25/09/2026, owner: "tự gỡ quà khi khách giảm số lượng, để tránh bị lợi dụng").
 * v2 26/09/2026 (owner: "chắc là logic quà tặng bundle không có lỗ thủng"): checkout gate + top-up.
 * v3 29/09/2026 (owner: "logic bên kaching, kết hợp chung với của mình"): gifts are CHILD lines of their
 * product (nested cart lines), every product is its own bundle, and + / − / remove on a product line go out
 * as ONE request with the numbers changing at once (see the notes above earnedBy and the fast path).
 * v4 30/09/2026 (owner: "đồng ý và ebook chỉ cần tặng 1"): combo scaling above Buy 3 - 4 units earn the Buy 3 +
 * Buy 1 gift sets, 5 = Buy 3 + Buy 2, 6 = 2 x Buy 3 (see comboGifts); the eBooks stay one per order.
 * v5 30/09/2026 (owner: "có nha"): rule #2 - raising a gift above what is earned makes the excess a PAID line of
 * its real twin (rules.real; only the Double-Sided Board has one on sale), rule #4 - a re-added real item turns
 * back into a deleted gift. Gifts with no twin keep + stopped at what is earned.
 *
 * The bundle block (st-bundle) adds the tier's gifts as $0 lines together with the pans / boards.
 * This script keeps every gift line at what the main products in the cart earn:
 *
 *  1. LOWER - after every cart change, on page load and right before checkout, any gift line above
 *     what the cart earns is set down (last lines first).
 *  2. TOP UP - when what the cart earns goes UP (a pan added from a collection card, 1 -> 2 boards with
 *     the stepper, a second Buy 1 ...), the missing gifts are added, never more than earned. A gift the
 *     shopper removed while the cart kept earning it is NOT added back (it only returns once the cart
 *     earns more of it again) - the Lavender rule the SCENTREAT owner set on 14/09.
 *  3. CHECKOUT GATE - the Check out button posts the cart form, and that form carries every quantity
 *     box as updates[]: typing 5 in a gift box and pressing Check out used to reach checkout before any
 *     check ran, and so did a click in the ~1.5 s between lowering a pan and this script's fix
 *     (both reproduced on live 26/09). The submit is held until every cart request has settled and a
 *     fresh check against the server cart has run, then sent WITHOUT the updates[] boxes.
 *     Wallet buttons on /cart (iframes, their clicks cannot be held) are inert while a check is due.
 *
 * What a cart earns comes from the rules printed by snippets/st-gift-guard.liquid, generated by
 * port/phase2/gift-guard-v2-2026-09-26.js from the st-bundle settings of each product template:
 *   ladder   - per main product: the tier (units) each gift unlocks at
 *   tiers    - per main product: units -> the gift set of that tier (Buy 1 / 2 / 3); above the top tier the
 *              units are split into tiers, biggest first, and the sets add up (combo scaling, 30/09/2026)
 *   perUnit  - one gift per unit (the pan's glass lid)
 *   perOrder - at most one per order whatever earns it (the eBooks; owner 26/09: "ebook 1 cuốn, gift thì 2",
 *              30/09: "ebook chỉ cần tặng 1")
 *   variants - gift handle -> variant id, for top-ups
 *   real     - gift handle -> {variant, handle} of the same item on sale (rule #2 paid excess, #4 claim back)
 * Units of a "+" bundle variant ("S + M + L") count as one per size.
 *
 * Limit: this runs in the browser. A cart link (/cart/<id>:1) or a script that skips the storefront
 * reaches checkout without it - the Flow order check is the net for those.
 */
if (!window.__stGiftGuard) {
  window.__stGiftGuard = true;

  const SOURCE = 'st-gift-guard';
  const STATE_KEY = 'st-gift-guard:v3';
  let rules = null;
  try {
    rules = JSON.parse(document.getElementById('st-gift-rules')?.textContent || 'null');
  } catch {
    rules = null;
  }

  if (rules && rules.mains) {
    const root = document.documentElement;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    let seq = 0; // bumped on every cart:update, ours included
    let owed = true; // a check is due (page load, or the cart changed since the last one)
    let running = null; // the check in progress
    let failures = 0;
    let timer = 0;
    let inflight = 0; // cart mutations on the wire, from any script
    let lastMutation = 0;
    let lastQuantity = 0; // Horizon sends a typed quantity 300 ms after the box changes

    const pending = () => owed || !!running || inflight > 0 || root.hasAttribute('data-st-fast') || Date.now() - lastQuantity < 450;
    const paint = () => root.toggleAttribute('data-st-gift-pending', pending());

    // ---- every cart mutation on the wire, whoever sent it (theme, apps, this file) ----
    const MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
    const nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      const promise = nativeFetch.apply(this, arguments);
      try {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || '';
        const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
        if (method !== 'GET' && MUTATION.test(new URL(url, location.href).pathname)) {
          inflight++;
          lastMutation = Date.now();
          paint();
          const done = () => {
            inflight--;
            lastMutation = Date.now();
            paint();
          };
          promise.then(done, done);
        }
      } catch {}
      return promise;
    };

    const units = (item) => {
      const title = String(item.variant_title || '');
      const sizes = title.includes('+') ? title.split('+').length : 1;
      return item.quantity * sizes;
    };

    // ---- v3 29/09/2026: gifts ride as CHILD lines of their product (Shopify nested cart lines, the Kaching
    // way): remove the pan and Shopify removes its gifts with it, and the drawer / checkout list them under
    // the product they belong to. Each product is its own bundle ("mỗi sp là 1 bundle riêng", owner 29/09):
    // what a cart earns is counted per (product, gift) pair.
    // Shopify facts this relies on (measured on this store 29/09): a child added with parent_id = a variant in
    // the SAME request attaches to that line; an existing line can only be referenced by parent_line_key;
    // line keys CHANGE after most cart edits (quantity, discount), so nothing here remembers keys - the memory
    // is per pair, by the variant of the line that holds the gifts; children never follow the parent's
    // quantity; a child can be removed on its own.

    let lastCart = null; // the latest full cart this script saw - the fast +/- path plans from it
    const keyVariant = new Map(); // every line key ever seen -> its variant (rows keep old keys until the repaint)
    const remember = (cart) => {
      if (!cart?.items) return;
      lastCart = cart;
      for (const item of cart.items) keyVariant.set(item.key, item.variant_id);
    };

    /** "product>gift" -> quantity the cart earns */
    const PAIR = (main, gift) => `${main}>${gift}`;

    // <combo> 30/09/2026 combo scaling (owner: "đồng ý và ebook chỉ cần tặng 1"; skill gift-rules.md rule #7).
    // The paid units of ONE product are split into its tiers, biggest first, again and again, and the gift sets
    // of the pieces add up: 4 = Buy 3 + Buy 1, 5 = Buy 3 + Buy 2, 6 = Buy 3 + Buy 3 (before 30/09, 4+ units kept
    // the Buy 3 set only). Tiers = rules.tiers[family], [{qty, gifts: {handle: n}}], printed by
    // snippets/st-gift-guard.liquid; a rules JSON without them falls back to the ladder's own tier quantities.
    // Units left below the smallest tier earn nothing. The Flow order check (gift-flow/build-flow-code.js)
    // pastes this same block, so the cart and the order are judged by one function.
    const comboGifts = (rules, family, n) => {
      let tiers = rules.tiers && rules.tiers[family];
      if (!Array.isArray(tiers) || !tiers.length) {
        const ladder = (rules.ladder && rules.ladder[family]) || {};
        tiers = [...new Set(Object.values(ladder).map(Number))].map((qty) => ({
          qty,
          gifts: Object.fromEntries(Object.entries(ladder).filter(([, from]) => Number(from) <= qty).map(([g]) => [g, 1])),
        }));
      }
      const out = {};
      let left = Math.max(0, Math.floor(Number(n) || 0));
      for (const t of tiers.filter((t) => Number(t.qty) >= 1).sort((a, b) => b.qty - a.qty)) {
        const times = Math.floor(left / t.qty);
        if (!times) continue;
        for (const [g, q] of Object.entries(t.gifts || {})) out[g] = (out[g] || 0) + (Number(q) || 0) * times;
        left -= times * t.qty;
      }
      return out;
    };
    // </combo>

    const earnedBy = (items, holds = new Map()) => {
      const perMain = new Map();
      for (const item of items) {
        if (!rules.mains[item.handle] || item.parent_relationship) continue;
        perMain.set(item.handle, (perMain.get(item.handle) || 0) + units(item));
      }
      const out = new Map();
      // perOrder gifts (the eBooks) stay on the product that already holds them, else go to the first product
      // in rules order - so adding a board to a pan cart does not move the eBooks from line to line
      const once = new Set();
      const order = Object.keys(rules.mains);
      for (const [gift, handle] of holds) if (perMain.has(handle)) order.sort((a, b) => (b === handle) - (a === handle));
      for (const handle of order) {
        const n = perMain.get(handle);
        if (!n) continue;
        const family = rules.mains[handle];
        const combo = comboGifts(rules, family, n);
        // ladder order first (the order the gift lines were always added in), then any gift only a tier names
        const names = [...new Set([...Object.keys(rules.ladder?.[family] || {}), ...Object.keys(combo)])];
        for (const gift of names) {
          const q = combo[gift] || 0;
          if (q <= 0) continue;
          if ((rules.perOrder || []).includes(gift)) {
            // the eBooks: ONE per order however many units or products earn them (owner 26/09, again 30/09)
            if (once.has(gift)) continue;
            once.add(gift);
            out.set(PAIR(handle, gift), 1);
            continue;
          }
          out.set(PAIR(handle, gift), q); // Mystery, knife, mat, double-sided board: combo count
        }
        const perUnit = rules.perUnit?.[family];
        if (perUnit) out.set(PAIR(handle, perUnit), n); // the pan's glass lid: one per pan, as before
      }
      return out;
    };

    // memory per cart, per pair: e = earned, h = gifts kept, p = {variant id of the holding line: qty}
    // (a new cart after an order starts from nothing; v3 key so a v2 memory is never misread)
    const readState = (token) => {
      try {
        const state = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
        return state && state.token === token ? state.pairs || {} : {};
      } catch {
        return {};
      }
    };
    const writeState = (token, pairs) => {
      try {
        sessionStorage.setItem(STATE_KEY, JSON.stringify({ token, pairs }));
      } catch {}
    };

    // ---- 30/09/2026 rule #2 + #4 (owner: "có nha"; skill gift-rules.md): a gift with a REAL twin on sale
    // (rules.real: gift handle -> {variant, handle}; on this store only the pan's Double-Sided Board gift ->
    // double-sided-titanium-cutting-board / Medium, same $119.95 value) follows the owner standard:
    //   #2 the customer raises the gift above what its product earns -> the gift stays at the earned quantity and
    //      the excess becomes a normal PAID line of the real item (never free, never silently dropped);
    //   #4 the customer deleted the gift and adds the real item again (its page, a cart link) -> up to what is
    //      earned it turns back into the free gift under its product; the rest stays paid.
    // Gifts with no real twin (Mystery, knife, mat, lid, eBooks) keep today's rule: + stops at what is earned.
    // "Real units added since the last check" are told apart from the ones already there (#2 paid units, a board
    // the customer bought for itself) by the memory's '#real' count.
    const REAL = rules.real || {};
    const CLAIM = new Map(Object.entries(REAL).map(([gift, r]) => [Number(r.variant), gift])); // real variant -> gift

    /** what to change: updates (line key -> qty), adds [{id, quantity, parentVariant}], paid [{id, quantity}]
     *  (real items, top-level lines, #2), next memory, again (a real line changed: check once more, so the real
     *  item's own bundle gifts follow). opt.noRaise: never turn a gift excess into a paid line (fast +/- path). */
    const plan = (cart, opt = {}) => {
      const items = cart.items || [];
      const byKey = new Map(items.map((item) => [item.key, item]));
      const holds = new Map(); // perOrder gift -> product that holds it now
      for (const item of items) {
        if (!(rules.perOrder || []).includes(item.handle) || holds.has(item.handle)) continue;
        const parent = byKey.get(item.parent_relationship?.parent_key || '');
        if (parent && rules.mains[parent.handle]) holds.set(item.handle, parent.handle);
      }
      const earned = earnedBy(items, holds);
      // the line new gifts hang on, per product: its biggest line (ties: the first one)
      const anchor = new Map();
      for (const item of items) {
        if (!rules.mains[item.handle] || item.parent_relationship) continue;
        const a = anchor.get(item.handle);
        if (!a || item.quantity > a.quantity) anchor.set(item.handle, item);
      }
      const mainVariants = new Set(items.filter((i) => rules.mains[i.handle] && !i.parent_relationship).map((i) => i.variant_id));
      const unitsOf = {}; // paid units per product (top-level lines), for the 12/10/2026 refill
      for (const i of items) if (rules.mains[i.handle] && !i.parent_relationship) unitsOf[i.handle] = (unitsOf[i.handle] || 0) + i.quantity;

      const updates = {};
      const linesOf = new Map(); // pair -> gift lines under that product
      const orphan = new Map(); // gift -> qty on lines under no product (old carts, cart links)
      for (const item of items) {
        if (!rules.gifts.includes(item.handle)) continue;
        const parent = byKey.get(item.parent_relationship?.parent_key || '');
        if (parent && rules.mains[parent.handle]) {
          const k = PAIR(parent.handle, item.handle);
          if (!linesOf.has(k)) linesOf.set(k, []);
          linesOf.get(k).push(item);
        } else {
          // re-attached below when the cart earns it, dropped otherwise
          orphan.set(item.handle, (orphan.get(item.handle) || 0) + item.quantity);
          updates[item.key] = 0;
        }
      }

      const before = readState(cart.token);
      // real twins on top-level lines now (#4), and how many of them the last check already knew about
      const realLines = new Map(); // gift -> [{line, left}]
      const realNow = {};
      for (const item of items) {
        const gift = CLAIM.get(item.variant_id);
        if (!gift || item.parent_relationship) continue;
        if (!realLines.has(gift)) realLines.set(gift, []);
        realLines.get(gift).push({ line: item, left: item.quantity });
        realNow[gift] = (realNow[gift] || 0) + item.quantity;
      }
      const known = before['#real'];
      const hadMemory = Object.keys(before).length > 0;
      const claimable = {};
      // a memory from before 30/09 has no '#real': everything already in the cart counts as known (never claimed)
      for (const [g, n] of Object.entries(realNow)) claimable[g] = Math.max(0, n - (known ? Number(known[g]) || 0 : hadMemory ? n : 0));
      const realAfter = { ...realNow };
      const paid = [];
      let again = false;

      // 1. LOWER - per pair, anything above what that product earns (last lines first)
      const have = new Map();
      const holders = new Map(); // pair -> {variant: qty}
      for (const [k, lines] of linesOf) {
        const n = earned.get(k) || 0;
        let left = n;
        let total = 0;
        for (const line of lines) {
          total += line.quantity;
          const keep = Math.min(line.quantity, left);
          left -= keep;
          if (keep !== line.quantity) updates[line.key] = keep;
          if (keep > 0) {
            have.set(k, (have.get(k) || 0) + keep);
            const v = byKey.get(line.parent_relationship.parent_key).variant_id;
            if (!holders.has(k)) holders.set(k, {});
            holders.get(k)[v] = (holders.get(k)[v] || 0) + keep;
          }
        }
        // #2: the part of the excess the CUSTOMER put on the gift line (it rose above what the last check kept)
        // becomes a paid line of the real item. The rest (#5: the product earns less now, a PDP add that brought
        // one gift too many) is only trimmed. No memory for this pair = never charge.
        const gift = k.split('>')[1];
        const b = before[k];
        if (!opt.noRaise && b && REAL[gift] && total > n) {
          const convert = Math.max(0, Math.min(total - n, total - (Number(b.h) || 0)));
          if (convert > 0) {
            paid.push({ id: Number(REAL[gift].variant), quantity: convert, pair: k, gift });
            realAfter[gift] = (realAfter[gift] || 0) + convert;
            again = true;
          }
        }
      }

      // 2. ADD - (a) orphans the cart earns, re-attached; (b) what the product earns MORE of since the last
      //    check (quick add, stepper); (c) gifts that vanished WITH their line (that line removed, or its size
      //    swapped) while the product still earns them; (d) #4: a real item added since the last check fills a
      //    gift the customer had deleted (the real line goes down, the free gift goes under the product). A gift
      //    the shopper removed while its line stayed is not added back otherwise.
      const adds = [];
      const next = {};
      for (const [k, n] of earned) {
        const [main, gift] = k.split('>');
        const got = have.get(k) || 0;
        const missing = n - got;
        const b = before[k] || {};
        let add = 0;
        if (missing > 0) {
          const adopt = Math.min(missing, orphan.get(gift) || 0);
          orphan.set(gift, (orphan.get(gift) || 0) - adopt);
          const rise = n - (Number(b.e) || 0);
          // owner 12/10/2026 ("quà tặng cho khách cứ add mới là có"): the paid units went up since the last check (a new add
          // from its page, + in the cart, an upsell) -> every gift it earns comes back, also one deleted earlier
          const grew = b.u !== undefined && (unitsOf[main] || 0) > Number(b.u);
          let lost = 0;
          for (const [v, q] of Object.entries(b.p || {})) if (!mainVariants.has(Number(v))) lost += Number(q) || 0;
          add = grew ? missing : Math.min(missing, adopt + Math.max(rise, Math.min(lost, (Number(b.h) || 0) - got), 0));
        }
        const id = Number(rules.variants?.[gift] || 0);
        const parent = anchor.get(main);
        // (d) #4 claim: only real units the last check did not know, only up to what is still missing
        if (missing - add > 0 && id && parent && REAL[gift] && !opt.noClaim) {
          let claim = Math.min(missing - add, claimable[gift] || 0);
          for (const r of realLines.get(gift) || []) {
            if (claim <= 0) break;
            const q = Math.min(r.left, claim);
            if (q <= 0) continue;
            r.left -= q;
            claim -= q;
            claimable[gift] -= q;
            realAfter[gift] -= q;
            updates[r.line.key] = r.left;
            add += q;
            again = true; // the real item is a product with its own bundle: its gifts follow on the next check
          }
        }
        const p = { ...(holders.get(k) || {}) };
        if (add > 0 && id && parent) {
          adds.push({ id, quantity: add, parentVariant: parent.variant_id, pair: k });
          p[parent.variant_id] = (p[parent.variant_id] || 0) + add;
        } else add = 0;
        next[k] = { e: n, h: got + add, p, u: unitsOf[main] || 0 };
      }
      const real = {};
      for (const [g, n] of Object.entries(realAfter)) if (n > 0) real[g] = n;
      next['#real'] = real;
      return { updates, adds, paid, next, again };
    };

    const sectionIds = () =>
      [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);

    const post = async (url, payload) => {
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await response.json();
      if (!response.ok || data?.status) throw new Error(data?.description || data?.message || `${url} failed`);
      return data;
    };

    /** hang the gifts on their product lines; keys are looked up in the cart as it is NOW */
    const addGifts = async (adds, cart) => {
      const keyOf = (variant) => (cart.items || []).find((i) => i.variant_id === variant && !i.parent_relationship)?.key;
      const items = adds.map((a) => ({ id: a.id, quantity: a.quantity, parent_line_key: keyOf(a.parentVariant) })).filter((i) => i.parent_line_key);
      if (!items.length) return;
      try {
        await post(Theme.routes.cart_add_url, { items });
      } catch (error) {
        if (items.length === 1) throw error;
        // /cart/add.js is all-or-nothing: one gift that went unavailable must not hold back the others
        for (const item of items) await post(Theme.routes.cart_add_url, { items: [item] }).catch(() => {});
      }
    };

    const CART_JSON = 'st-cart-json';

    /** cart JSON (with parent keys) printed by sections/st-cart-json.liquid, shaped like /cart.js */
    const cartFromSection = (html, token) => {
      const m = /<script[^>]*data-st-cart-json[^>]*>([\s\S]*?)<\/script>/.exec(html || '');
      if (!m) return null;
      try {
        const c = JSON.parse(m[1]);
        c.token = token;
        c.items = (c.items || []).map(({ parent_key, ...item }) => (parent_key ? { ...item, parent_relationship: { parent_key } } : item));
        return c;
      } catch {
        return null;
      }
    };

    /** add the gifts (keys from `cart`, the cart as it is NOW) and repaint from the same answer: the drawer and
     *  st-cart-json come back with it, so no extra update / read is needed (falls back to one-by-one + a read) */
    const addAndRepaint = async (adds, cart, sections, paid = []) => {
      const keyOf = (variant) => (cart.items || []).find((i) => i.variant_id === variant && !i.parent_relationship)?.key;
      const items = adds.map((a) => ({ id: a.id, quantity: a.quantity, parent_line_key: keyOf(a.parentVariant) })).filter((i) => i.parent_line_key);
      // #2: the real items the customer asked for above what is earned ride in the same request, as their own lines
      for (const p of paid) items.unshift({ id: p.id, quantity: p.quantity });
      if (items.length) {
        try {
          const res = await post(Theme.routes.cart_add_url, { items, ...sections });
          const data = cartFromSection(res.sections?.[CART_JSON], cart.token);
          if (data) return { ...data, sections: res.sections };
        } catch {
          for (const item of items) await post(Theme.routes.cart_add_url, { items: [item] }).catch(() => {});
        }
      }
      return post(Theme.routes.cart_update_url, { updates: {}, ...sections });
    };

    // 3 failures in a row pause the automatic checks for 20 s (a network blip must not switch them off for
    // good); the checkout gate always tries once more
    let lastFailure = 0;
    const check = async (force = false) => {
      if (!force && failures >= 3 && Date.now() - lastFailure < 20000) {
        owed = false;
        return;
      }
      owed = false;
      try {
        const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
        remember(cart);
        const { updates, adds, paid, next, again } = plan(cart);
        const lowering = Object.keys(updates).length > 0;
        if (!lowering && !adds.length && !paid.length) {
          writeState(cart.token, next);
          failures = 0;
          return;
        }

        const seqAtStart = seq;
        const ids = sectionIds();
        const sections = { sections: [...ids, CART_JSON].join(','), sections_url: window.location.pathname };
        const adding = adds.length || paid.length;
        // lower first: its answer is the whole cart with fresh line keys for the adds
        let data = lowering ? await post(Theme.routes.cart_update_url, { updates, ...(adding ? {} : sections) }) : cart;
        // the gift add (and the #2 paid real items) brings back the whole cart and the drawer sections for the repaint
        if (adding) data = await addAndRepaint(adds, data, sections, paid);
        writeState(data.token || cart.token, next);
        // a real item changed (#2 paid, #4 claimed): it is a product with its own bundle - check once more
        if (again) setTimeout(schedule, 0);
        if (data.items) remember({ ...data, token: data.token || cart.token });
        failures = 0;
        // a newer cart change started meanwhile: announce the cart, let that change paint the drawer
        if (seq !== seqAtStart) data.sections = undefined;
        else if (data.sections) delete data.sections[CART_JSON];
        document.dispatchEvent(
          new CartUpdateEvent(data, SOURCE, { source: SOURCE, itemCount: data.item_count, sections: data.sections })
        );
      } catch (error) {
        failures++;
        lastFailure = Date.now();
        console.warn('[st-gift-guard]', error);
      }
    };

    /** run a check now; if one is running, wait for it first so this one sees the latest cart */
    const runNow = async (force = false) => {
      clearTimeout(timer);
      while (running) await running;
      running = check(force);
      paint();
      try {
        await running;
      } finally {
        running = null;
        paint();
      }
    };

    const schedule = () => {
      clearTimeout(timer);
      owed = true;
      paint();
      timer = setTimeout(runNow, 250);
    };

    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      seq++;
      if (event.detail?.data?.source === SOURCE) return;
      schedule();
    });

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => {
      lastQuantity = Date.now();
      paint();
      setTimeout(paint, 500);
    });

    // ---- fast + / − / remove (29/09/2026, owner: "tốc độ mượt sẽ giúp trải nghiệm người dùng tốt hơn") ----
    // On a product line in the drawer or /cart: the number changes at once (spinner on the button, Check out held
    // by the gate below), and ONE request carries the product and its gifts together - before, Horizon waited
    // 300 ms, sent its change, then this guard read the cart, added the gifts and repainted (≈ 4 s to see the
    // new gifts). + goes out as /cart/add.js: the extra units of that line and the gifts they earn, hung on that
    // same line (parent_id may name a variant sent in the same request). − / remove go out as /cart/update.js with
    // the product and the gift lines in one body. Quick clicks queue; only the last answer repaints the drawer
    // (a repaint without sections makes Horizon re-download the drawer). Gift lines, Shipping Protection and
    // typed quantities keep the theme's own path.
    const want = new Map(); // variant id of a product line -> quantity the shopper wants
    const paidWant = new Map(); // 30/09/2026 #2: real variant -> extra PAID units asked for with the + of its gift
    let pumping = null;
    let checkAfterFast = false; // a real item changed: the guard checks once more when the queue is done

    const clearBusy = () => {
      if (pumping || want.size || paidWant.size) return;
      for (const el of document.querySelectorAll('cart-items-component .st-busy')) el.classList.remove('st-busy');
      for (const el of document.querySelectorAll('cart-items-component .st-removing')) el.classList.remove('st-removing');
      root.removeAttribute('data-st-fast');
      paint();
    };

    const sendFast = async (cart, line, qty) => {
      // the cart as it will be: this line at the new quantity (its gifts leave with it when it goes)
      const sim = { ...cart, items: [] };
      for (const item of cart.items) {
        if (item.key === line.key) {
          if (qty > 0) sim.items.push({ ...item, quantity: qty });
        } else if (!(qty === 0 && item.parent_relationship?.parent_key === line.key)) sim.items.push(item);
      }
      // noRaise: a + / − on a product never turns a gift excess into a paid line (only the customer's own
      // + / typed quantity on a gift line does, rule #2)
      const { updates, adds, next, again } = plan(sim, { noRaise: true });
      if (again) checkAfterFast = true;
      const ids = sectionIds();
      const sections = { sections: [...ids, CART_JSON].join(','), sections_url: window.location.pathname };
      let data;
      let drawer;
      if (qty > line.quantity && !Object.keys(updates).length) {
        const items = [{ id: line.variant_id, quantity: qty - line.quantity }];
        for (const a of adds) {
          items.push({ id: a.id, quantity: a.quantity, parent_id: line.variant_id });
          // the memory: these gifts now sit on this line, not on the planner's anchor
          const p = next[a.pair].p;
          p[a.parentVariant] = (p[a.parentVariant] || 0) - a.quantity;
          if (p[a.parentVariant] <= 0) delete p[a.parentVariant];
          p[line.variant_id] = (p[line.variant_id] || 0) + a.quantity;
        }
        const res = await post(Theme.routes.cart_add_url, { items, ...sections });
        drawer = res.sections;
        data = cartFromSection(res.sections?.[CART_JSON], cart.token);
      } else {
        const body = { ...updates, [line.key]: qty };
        if (qty === 0) for (const item of cart.items) if (item.parent_relationship?.parent_key === line.key) delete body[item.key];
        const res = await post(Theme.routes.cart_update_url, { updates: body, ...(adds.length ? {} : sections) });
        data = res;
        drawer = res.sections;
        if (adds.length) {
          // the line that held the gifts went: hang them on the line that is left, repaint from that answer
          const after = await addAndRepaint(adds, res, sections);
          data = after;
          drawer = after.sections;
        }
      }
      writeState(cart.token, next);
      if (data?.items) remember({ ...data, token: data.token || cart.token });
      else lastCart = null;
      return { data, drawer };
    };

    /** rule #2 (30/09/2026): the + of a gift already at what its product earns, for a gift with a real twin on sale:
     *  ONE /cart/add.js with the real item as a normal paid line (same size, full price; the gift line stays as
     *  it is). The real item is a product with its own bundle, so the guard checks once more afterwards and its
     *  gifts follow. The memory learns these paid units ('#real'), so deleting the gift later never makes them
     *  free again - only a real item added AFTER that does (#4). */
    const sendPaid = async () => {
      const items = [...paidWant.entries()].map(([id, quantity]) => ({ id, quantity }));
      paidWant.clear();
      const ids = sectionIds();
      const sections = { sections: [...ids, CART_JSON].join(','), sections_url: window.location.pathname };
      const token = lastCart?.token;
      const res = await post(Theme.routes.cart_add_url, { items, ...sections });
      const data = cartFromSection(res.sections?.[CART_JSON], token);
      if (data?.items && token) {
        const state = readState(token);
        const real = { ...(state['#real'] || {}) };
        for (const [gift, r] of Object.entries(REAL)) {
          const n = data.items.reduce((s, i) => s + (i.variant_id === Number(r.variant) && !i.parent_relationship ? i.quantity : 0), 0);
          if (n > 0) real[gift] = n;
        }
        writeState(token, { ...state, '#real': real });
        remember(data);
      } else lastCart = null;
      checkAfterFast = true;
      return { data, drawer: res.sections };
    };

    const pump = () =>
      pumping ||
      (pumping = (async () => {
        let last = null;
        try {
          while (want.size || paidWant.size) {
            if (!want.size) {
              last = await sendPaid();
              continue;
            }
            const [variant, qty] = want.entries().next().value;
            want.delete(variant);
            const cart = lastCart || (await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json());
            remember(cart);
            const line = (cart.items || []).find((i) => i.variant_id === variant && !i.parent_relationship);
            if (!line || line.quantity === qty) continue;
            last = await sendFast(cart, line, qty);
          }
          if (last) {
            const { data, drawer } = last;
            const sectionsOut = { ...(drawer || {}) };
            delete sectionsOut[CART_JSON];
            document.dispatchEvent(
              new CartUpdateEvent(data || {}, SOURCE, { source: SOURCE, itemCount: data?.item_count, sections: sectionsOut })
            );
          }
        } catch (error) {
          console.warn('[st-gift-guard] fast path', error);
          lastCart = null;
          want.clear();
          paidWant.clear();
          // repaint from the server (no sections -> the theme re-renders the drawer) and let the guard check it
          document.dispatchEvent(new CartUpdateEvent({}, SOURCE, { source: SOURCE }));
          schedule();
        } finally {
          pumping = null;
          clearBusy();
          if (checkAfterFast && !want.size && !paidWant.size) {
            checkAfterFast = false;
            schedule();
          }
        }
      })());

    window.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const button = target?.closest(
          'cart-items-component button[name="plus"], cart-items-component button[name="minus"], cart-items-component .cart-items__remove'
        );
        if (!button || !lastCart) return; // no cart seen yet: the theme's path, the guard follows
        const row = button.closest('[data-key]');
        const variant = keyVariant.get(row?.dataset.key || '');
        if (!variant) return;
        const cart = lastCart;
        const item = (cart.items || []).find((i) => i.variant_id === variant && (i.key === row.dataset.key || !i.parent_relationship));
        if (!item) return;
        const input = row.querySelector('input[name="updates[]"], input[type="number"]');

        if (rules.gifts.includes(item.handle)) {
          // + on a gift: inside what its product earns -> the theme's path. At the limit: a gift with a real twin
          // on sale becomes a PAID line of the real item (rule #2, 30/09/2026); any other gift stays as it is.
          if (button.getAttribute('name') !== 'plus') return;
          const parent = (cart.items || []).find((i) => i.key === item.parent_relationship?.parent_key);
          const earned = parent ? earnedBy(cart.items).get(PAIR(parent.handle, item.handle)) || 0 : 0;
          // the whole pair counts (the same gift can sit on two lines of one product), this row as typed
          let pairQty = 0;
          if (parent) {
            for (const i of cart.items || []) {
              const p = i.handle === item.handle && (cart.items || []).find((x) => x.key === i.parent_relationship?.parent_key);
              if (p && p.handle === parent.handle) pairQty += i.key === item.key ? Number(input?.value || i.quantity) || 0 : i.quantity;
            }
          }
          if (parent && pairQty < earned) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          const real = parent && rules.mains[parent.handle] ? REAL[item.handle] : null;
          if (!real) return; // no real twin on sale (Mystery, knife, mat, lid, eBooks): nothing to sell
          paidWant.set(Number(real.variant), (paidWant.get(Number(real.variant)) || 0) + 1);
          button.classList.add('st-busy');
          root.setAttribute('data-st-fast', '');
          lastMutation = Date.now();
          paint();
          pump();
          return;
        }
        if (!rules.mains[item.handle] || item.parent_relationship) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        const name = button.getAttribute('name');
        const current = want.has(variant) ? want.get(variant) : Number(input?.value || item.quantity) || 0;
        const qty = name === 'plus' ? current + 1 : name === 'minus' ? Math.max(0, current - 1) : 0;
        want.set(variant, qty);
        if (input && qty > 0) input.value = String(qty);
        if (qty === 0) row.classList.add('st-removing');
        button.classList.add('st-busy');
        root.setAttribute('data-st-fast', '');
        lastMutation = Date.now();
        paint();
        pump();
      },
      true
    );

    // ---- checkout gate ----
    let released = false;
    let holding = false;
    const neutralise = (form) => {
      for (const el of form.elements) if (el.name === 'updates[]') el.disabled = true;
    };
    // a quantity typed into a box and not sent yet (Check out pressed straight away): send it first, through
    // the cart API, so the check below sees it - the box itself is never posted
    const pushTyped = async (form) => {
      const updates = {};
      for (const el of form.elements) {
        if (el.name !== 'updates[]' || el.disabled) continue;
        const key = el.closest('[data-key]')?.dataset.key;
        const now = el.value === '' ? NaN : Number(el.value);
        if (key && Number.isInteger(now) && now >= 0 && now !== Number(el.defaultValue)) updates[key] = now;
      }
      if (Object.keys(updates).length) await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ updates }) }));
    };
    window.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        const submitter = event.submitter;
        if (submitter ? submitter.name !== 'checkout' : form.id !== 'cart-form') return;
        if (released) {
          // the gated submit going out: quantities are already on the server, the boxes must not overwrite them
          released = false;
          neutralise(form);
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (holding) return; // a second click while the first one is being checked
        holding = true;
        submitter?.setAttribute('aria-busy', 'true');

        const until = Date.now() + 6000;
        const quiet = () => inflight === 0 && Date.now() - lastMutation > 350 && Date.now() - lastQuantity > 450;
        const hold = async () => {
          await sleep(60); // a typed quantity dispatches its change on blur, just before this submit
          while (!quiet() && Date.now() < until) await sleep(50);
          await pushTyped(form);
          while (inflight > 0 && Date.now() < until) await sleep(50);
          await runNow(true);
          while (inflight > 0 && Date.now() < until) await sleep(50);
        };
        // never keep a buyer from checkout: a slow network lets the order through after 8 s
        await Promise.race([hold().catch(() => {}), sleep(8000)]);

        // the drawer may have been repainted meanwhile: submit the form / button that are on the page now
        const liveForm = (form.isConnected ? form : document.getElementById(form.id)) || form;
        const button = [...document.querySelectorAll('[name="checkout"]')].find((b) => b.form === liveForm);
        // disabled boxes are left out of the post AND out of form validation
        neutralise(liveForm);
        released = true;
        try {
          if (button && liveForm.requestSubmit) liveForm.requestSubmit(button);
          else throw new Error('no requestSubmit');
        } catch {
          const flag = document.createElement('input');
          flag.type = 'hidden';
          flag.name = 'checkout';
          flag.value = '1';
          liveForm.appendChild(flag);
          liveForm.submit();
        }
        released = false; // requestSubmit dispatches synchronously; never let the flag outlive this call
        holding = false;
        submitter?.removeAttribute('aria-busy');
      },
      true
    );
    // back/forward cache: a page restored after checkout must not keep its boxes disabled
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      for (const el of document.querySelectorAll('[name="updates[]"]')) el.disabled = false;
      schedule();
    });

    // also on page load: a cart changed in another tab or before this script existed
    schedule();
  }
}
